<template>
  <div class="plan-comparator">
    <!-- Migration mode : show the user's current V4 offer up top so the V5
         grid below is unambiguous (no overlap with the "current" pill). -->
    <div
      v-if="migrationMode && currentOfferBanner"
      class="plan-comparator__current-banner"
    >
      <div class="plan-comparator__current-banner-icon">
        <i class="fa-regular fa-clipboard-list-check" />
      </div>
      <div class="plan-comparator__current-banner-body">
        <div class="plan-comparator__current-banner-label">
          {{ t('billing.compare.currentOfferLabel') }}
        </div>
        <div class="plan-comparator__current-banner-title">
          {{ currentOfferBanner.planName }}
          <!-- V3 legacy bundles have no comparable monthly price — the
               banner then shows the offer name alone. -->
          <span
            v-if="currentOfferBanner.priceLabel"
            class="plan-comparator__current-banner-price"
          >
            · {{ currentOfferBanner.priceLabel }}
          </span>
        </div>
        <div
          v-if="currentOfferBanner.subtitle"
          class="plan-comparator__current-banner-subtitle"
        >
          {{ currentOfferBanner.subtitle }}
        </div>
      </div>
    </div>

    <!-- Top : frequency toggle. Hidden on V5 — moved inside the green tier
         selector block to save vertical space (the contacts block already
         carries enough chrome). -->
    <div
      v-if="!tiers || !tiers.selectable"
      class="plan-comparator__frequency"
    >
      <div class="plan-comparator__pill-group">
        <Button
          type="button"
          class="plan-comparator__pill"
          :class="{ 'plan-comparator__pill--active': localFrequency === 'monthly' }"
          @click="setFrequency('monthly')"
        >
          {{ t('billing.compare.monthly') }}
        </Button>
        <Button
          type="button"
          class="plan-comparator__pill"
          :class="{ 'plan-comparator__pill--active': localFrequency === 'yearly' }"
          @click="setFrequency('yearly')"
        >
          {{ t('billing.compare.monthly_yearly_short') }}
          <span class="plan-comparator__pill-badge">
            -15%
          </span>
        </Button>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="plan-comparator__loading"
    >
      <ProgressSpinner style="width: 36px; height: 36px" />
    </div>

    <template v-else-if="tiers">
      <!-- Top bar (V5 only) — 3-column strip: contacts count, tier dropdown
           (with the auto-tier-upgrade controls nested right under it), and
           the billing cycle. The dropdown updates `selectedTier`, which
           drives the displayed price in every plan card simultaneously. -->
      <div
        v-if="tiers.selectable"
        class="plan-comparator__topbar"
      >
        <div class="plan-comparator__topbar-info">
          <i class="fa-regular fa-users plan-comparator__topbar-icon" />
          <span class="plan-comparator__topbar-text">
            {{ t('billing.compare.contactsInBase', { count: formatNumber(tiers.currentMetric.value) }) }}
          </span>
        </div>
        <div class="plan-comparator__topbar-tier">
          <label class="plan-comparator__topbar-eyebrow">
            {{ t('billing.compare.tier.chooseLabel') }}
          </label>
          <Dropdown
            v-model="selectedTier"
            :options="tierOptions"
            option-label="label"
            option-value="value"
            class="plan-comparator__topbar-dropdown"
          />
          <!-- Compact auto-tier-upgrade controls — embedded under the
               dropdown so the customer reads "tier + auto-upgrade" as one
               coherent decision. Hidden in migration mode (no auto-tier UX
               yet) and in yearly (no self-served subscription). -->
          <TierAutoUpgradeBlock
            v-if="showAutoTierBlock"
            :current-tier="selectedTier ?? 0"
            :tiers="tiers.tiers"
            :enabled="tierAutoUpgradeEnabled"
            :max-tier="tierAutoUpgradeMax"
            @update:enabled="$emit('update:tierAutoUpgradeEnabled', $event)"
            @update:maxTier="$emit('update:tierAutoUpgradeMax', $event)"
          />
        </div>
        <div class="plan-comparator__topbar-frequency">
          <label class="plan-comparator__topbar-eyebrow">
            {{ t('billing.compare.frequencyLabel') }}
          </label>
          <div class="plan-comparator__pill-group plan-comparator__pill-group--compact">
            <Button
              type="button"
              class="plan-comparator__pill"
              :class="{ 'plan-comparator__pill--active': localFrequency === 'monthly' }"
              @click="setFrequency('monthly')"
            >
              {{ t('billing.compare.monthly') }}
            </Button>
            <Button
              type="button"
              class="plan-comparator__pill"
              :class="{ 'plan-comparator__pill--active': localFrequency === 'yearly' }"
              @click="setFrequency('yearly')"
            >
              {{ t('billing.compare.monthly_yearly_short') }}
              <span class="plan-comparator__pill-badge">
                -15%
              </span>
            </Button>
          </div>
        </div>
      </div>

      <!-- Read-only metric for V4 -->
      <div
        v-else
        class="plan-comparator__metric-readonly"
      >
        <i class="fa-regular fa-chart-line" />
        {{ tiers.currentMetric.tierLabel }}
      </div>

      <!-- Plans grid -->
      <div
        v-if="visiblePlans.length > 0"
        class="plan-comparator__plans"
      >
        <div
          v-for="plan in visiblePlans"
          :key="plan.code"
          class="plan-card"
          :class="{
            'plan-card--recommended': plan.code === recommendedPlan,
            'plan-card--selected': modelValue === plan.code,
            'plan-card--current': plan.isCurrent,
            'plan-card--contact': plan.isContactSales,
          }"
          tabindex="0"
          role="button"
          @click="plan.isContactSales ? openContactSales() : selectPlan(plan.code)"
          @keyup.enter="plan.isContactSales ? openContactSales() : selectPlan(plan.code)"
        >
          <div
            v-if="plan.code === recommendedPlan"
            class="plan-card__ribbon"
          >
            {{ t('billing.compare.recommended') }}
          </div>
          <div
            v-else-if="plan.isCurrent"
            class="plan-card__ribbon plan-card__ribbon--current"
          >
            {{ t('billing.compare.relation.current') }}
          </div>

          <div class="plan-card__header">
            <div class="plan-card__name">
              {{ plan.name }}
            </div>
            <p class="plan-card__tagline">
              {{ plan.tagline }}
            </p>
          </div>

          <div class="plan-card__price">
            <template v-if="plan.isContactSales || isYearlyDiscountContact">
              <!-- Pro plan : custom pricing, surfaced as "Nous contacter".
                   Also used for Standard/Premium/Ultimate when the cycle is
                   yearly AND a shop-level plan discount is active : the
                   yearly + discount combination is quote-only, so we hide
                   the price and route the customer to support, exactly like
                   the Entreprise card. -->
              <div class="plan-card__price-amount plan-card__price-amount--contact">
                {{ t('billing.compare.contactSales') }}
              </div>
              <div class="plan-card__price-period">
                {{ t('billing.compare.contactSalesPeriod') }}
              </div>
            </template>
            <template v-else>
              <!-- Standard / Premium / Ultimate : the catalog monthly price
                   is shown regardless of the chosen frequency. In yearly the
                   CTA below routes to Gleap (quote-only), but we keep the
                   monthly reference price visible to anchor the comparison.

                   When a shop-level `discountPercent` is active, we surface
                   both the original catalog price (struck through) AND the
                   discounted price + a "-X%" pill, so the comparison stays
                   honest across the three cards (same discount applied to
                   each catalog price). -->
              <div
                v-if="discountedPriceOf(plan.priceMonthly) !== null"
                class="plan-card__price-strike"
              >
                {{ formatPrice(plan.priceMonthly) }}
              </div>
              <div class="plan-card__price-amount">
                {{
                  formatPrice(
                    discountedPriceOf(plan.priceMonthly) ?? plan.priceMonthly,
                  )
                }}
                <span
                  v-if="discountedPriceOf(plan.priceMonthly) !== null"
                  class="plan-card__price-discount"
                >−{{ discountPercent }}%</span>
              </div>
              <div class="plan-card__price-period">
                {{ t('billing.compare.perMonth') }}
              </div>
            </template>
          </div>

          <ul class="plan-card__features">
            <li
              v-for="(feat, idx) in plan.features"
              :key="idx"
              class="plan-card__feature"
            >
              <i class="fa-regular fa-circle-check plan-card__feature-icon" />
              <span>{{ feat }}</span>
            </li>
          </ul>

          <Button
            class="plan-card__cta"
            :class="modelValue === plan.code ? 'p-button' : 'p-button-outlined'"
            :label="ctaLabel(plan)"
            :icon="(plan.isContactSales || localFrequency === 'yearly') ? 'fa-regular fa-message' : undefined"
            :disabled="ctaDisabled(plan)"
            @click.stop="(plan.isContactSales || localFrequency === 'yearly')
              ? openContactSales()
              : confirmPlan(plan.code)"
          />
        </div>
      </div>

      <div
        v-else
        class="plan-comparator__empty"
      >
        {{ t('billing.compare.empty') }}
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import ProgressSpinner from 'primevue/progressspinner';
import Gleap from 'gleap';
import { fetchPlansTiers } from '@/composables/billing/billingApi';
import { PlansTiersResponse } from '@/types/billing-types';
import TierAutoUpgradeBlock from '@/components/billing/panels/TierAutoUpgradeBlock.vue';

interface DisplayedPlan {
  code: string;
  name: string;
  tagline: string;
  /** Catalog monthly price. Only price displayed from the front — yearly
   *  subscriptions are quote-only (Gleap support), no client-side multiplier. */
  priceMonthly: number;
  features: string[];
  isCurrent: boolean;
  canResubscribe: boolean;
  /** True for plans without a public price (e.g. "Pro" V5) : the card
   *  displays "Nous contacter" instead of a price, and the CTA opens the
   *  support channel (Gleap) instead of moving to the next phase. */
  isContactSales: boolean;
  /** Position of this plan vs the user's current plan. `null` when the
   *  current plan is unknown (no active subscription, contact-sales plan). */
  relation: 'current' | 'upgrade' | 'downgrade' | null;
}

// Hierarchical ordering used to derive upgrade/downgrade vs the current plan.
// Keep `pro` at the top so the V5 contact-sales card is treated as the
// strongest upgrade path.
const PLAN_RANK: Record<string, number> = {
  standard: 1,
  premium: 2,
  ultimate: 3,
  pro: 4,
};

// Plan feature lists differ between V4 and V5 — even Standard and Premium,
// which exist in both, don't expose the same features. V4 reflects the legacy
// MyOffer14 catalog (extracted from configs/offers.ts, visible features only;
// API was hidden in legacy). V5 starts as a copy of V4 and adds Ultimate/Pro;
// product will refine V5 once the V5 marketing list is finalised.
//
// Each i18n key resolves under `billing.compare.feature.{v4|v5}.{key}`.
type OfferVersionKey = 'v4' | 'v5';
const PLAN_FEATURE_KEYS_BY_VERSION: Record<OfferVersionKey, Record<string, string[]>> = {
  v4: {
    standard: [
      'unlimitedEmails',
      'automation',
      'popins',
      'sms',
      'pushWeb',
      'listSegment',
      'multichannelRecommendation',
      'emailStats',
      'support',
    ],
    premium: [
      'standardPlus',
      'campaignsPush',
      'multiAccount',
      'export',
      'deliverabilityStats',
      'statBuilder',
      'appointmentCS',
      'telephoneSupport',
    ],
    // V4 backend never returns Ultimate / Pro — entries omitted on purpose.
  },
  v5: {
    standard: [
      'pos1',
      'user1',
      'customData2',
      'allChannels',
      'conditionalBlocks',
      'essentialStats',
      'helpcenter',
    ],
    premium: [
      'pos5',
      'users10',
      'customData10',
      'statBuilderFull',
      'exportData',
      'apiAccess',
      'preferentialMsgRates',
      'phoneSupport',
      'standardIncluded',
    ],
    ultimate: [
      'pos50',
      'usersUnlimited',
      'customData100',
      'premiumIncluded',
    ],
    pro: [
      'posUnlimited',
      'usersUnlimited',
      'customDataUnlimited',
      'ultimateIncluded',
      'customQuote',
    ],
  },
};

export default defineComponent({
  name: 'PlanComparator',

  components: {
    Button, Dropdown, ProgressSpinner, TierAutoUpgradeBlock,
  },

  props: {
    modelValue: {
      type: String as PropType<string | null>,
      default: null,
    },

    frequency: {
      type: String as PropType<'monthly' | 'yearly'>,
      default: 'monthly',
    },

    currentPlanCode: {
      type: String as PropType<string | null>,
      default: null,
    },

    /**
     * Force the comparator to load and render a different offer grid than the
     * shop's natural one. Only `'v5'` is supported today — used by
     * MigrateV5Dialog to let a V4 shop browse the V5 catalog.
     */
    offerOverride: {
      type: String as PropType<'v4' | 'v5' | null>,
      default: null,
    },

    /**
     * When true, the comparator switches to "migration" UX :
     *   - shows the {@link currentOfferBanner} above the tier selector,
     *   - hides the "current plan" pill on each plan card,
     *   - replaces upgrade/downgrade CTAs with a single « Migrer vers {plan} ».
     */
    migrationMode: {
      type: Boolean,
      default: false,
    },

    /**
     * Banner content displayed at the top of the comparator when
     * {@link migrationMode} is on. Lets the user see their current offer
     * (plan + monthly price) without it interfering with the new V5 grid.
     * `priceLabel` is optional: V3 legacy bundles have no comparable
     * monthly price, the banner then shows the offer name alone.
     */
    currentOfferBanner: {
      type: Object as PropType<{
        planName: string;
        priceLabel?: string;
        subtitle?: string;
      } | null>,

      default: null,
    },

    /**
     * V5 auto-tier-upgrade configuration. Two-way bound via the matching
     * emits below. Ignored in V4 (the block is hidden anyway). Defaults are
     * computed inside {@link TierAutoUpgradeBlock} so the parent can pass
     * `undefined` and let the child take care of `tierAutoUpgradeMax` init.
     */
    tierAutoUpgradeEnabled: {
      type: Boolean,
      default: true,
    },

    tierAutoUpgradeMax: {
      type: Number,
      default: 0,
    },

    /**
     * Shop-level active discount on the plan price (`discount.plan` in
     * `shops_plans.plan` JSON, surfaced as `overview.plan.discountPercent`).
     * When truthy and in the open interval (0, 100), each plan card shows
     * both the struck-through catalog price AND the discounted price plus
     * a "-X%" pill — same percent applied to all 3 plans so the
     * comparison stays apples-to-apples. Null / 0 / 100 ⇒ no discount UI.
     */
    discountPercent: {
      type: Number as PropType<number | null>,
      default: null,
    },
  },

  emits: [
    'update:modelValue',
    'update:frequency',
    'update:targetTier',
    'update:tierAutoUpgradeEnabled',
    'update:tierAutoUpgradeMax',
    'confirm',
    'error',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const tiers = ref<PlansTiersResponse | null>(null);
    const loading = ref(false);
    const localFrequency = ref<'monthly' | 'yearly'>(props.frequency);
    const selectedTier = ref<number | null>(null);

    const loadTiers = async () => {
      loading.value = true;
      try {
        const result = await fetchPlansTiers(
          props.offerOverride ? { overrideOffer: props.offerOverride } : {},
        );
        // Structured error from the back (e.g. PAGES_VIEWED_UNAVAILABLE for a
        // V4 shop whose page views are not yet computed) — forward the code
        // to the parent dialog so it can switch to a "Contact support" state
        // instead of leaving the modal empty. A `null` result (empty or
        // malformed response) is surfaced the same way: without the emit the
        // comparator renders nothing and the wizard body stays blank.
        if (!result || 'error' in result) {
          emit('error', (result && 'error' in result ? result.error.code : null) ?? 'UNKNOWN');
          tiers.value = null;
          return;
        }
        tiers.value = result as PlansTiersResponse | null;
        if (tiers.value) {
          // Default tier = the shop's natural tier (minimum tier covering the
          // current metric — contacts for V5, pages views for V4 -- back side).
          const naturalTier = tiers.value.tiers.find((t) => t.isCurrent);
          selectedTier.value = naturalTier
            ? naturalTier.threshold
            : tiers.value.tiers[0]?.threshold ?? null;
          emit('update:targetTier', selectedTier.value ?? undefined);
        }
      } finally {
        loading.value = false;
      }
    };

    /**
     * Tier minimum the shop is allowed to subscribe to — couvering its
     * current contacts count. Tiers below this threshold are hidden from the
     * dropdown (spec V5-d): the customer can never downgrade to a tier that
     * would not cover their current usage.
     */
    const minSelectableTier = computed<number | null>(() => (
      tiers.value?.currentMetric?.tierUpperBound ?? null
    ));

    const setFrequency = (f: 'monthly' | 'yearly') => {
      localFrequency.value = f;
      emit('update:frequency', f);
    };

    const currentTier = computed(() => {
      if (!tiers.value || selectedTier.value === null) return null;
      return tiers.value.tiers.find((t) => t.threshold === selectedTier.value) ?? null;
    });

    const tierOptions = computed(() => {
      if (!tiers.value) return [];
      // Hide tiers below the shop's current usage (spec V5-d): the customer
      // sees only its natural tier + every higher tier. `minSelectableTier`
      // is null in V4 (selectable=false), in which case we don't render the
      // dropdown at all, so the filter is a no-op.
      const min = minSelectableTier.value;
      return tiers.value.tiers
        .filter((tier) => min == null || tier.threshold >= min)
        .map((tier) => ({
          value: tier.threshold,
          label: t('billing.compare.tier.option', { count: formatNumber(tier.threshold) }),
        }));
    });

    // Guard: if for any reason `selectedTier` ends up below `minSelectableTier`
    // (legacy state, prop override, etc.), snap it up to the natural tier so
    // the dropdown never displays a value it doesn't propose.
    watch([minSelectableTier, selectedTier], ([min, current]) => {
      if (min == null || current == null) return;
      if (current < min) {
        selectedTier.value = min;
        emit('update:targetTier', min);
      }
    });

    const visiblePlans = computed<DisplayedPlan[]>(() => {
      if (!currentTier.value || !tiers.value) return [];
      const version: OfferVersionKey = tiers.value.pricingModel === 'contacts' ? 'v5' : 'v4';
      const versionCatalog = PLAN_FEATURE_KEYS_BY_VERSION[version];

      // Migration mode lives on a *different* offer grid than the user's
      // current plan — the V4 "premium" code happens to match V5 "premium"
      // but the underlying offer is different, so the current/upgrade/
      // downgrade relation is meaningless here.
      const currentRank = !props.migrationMode && props.currentPlanCode
        ? PLAN_RANK[props.currentPlanCode] ?? null
        : null;

      const plans: DisplayedPlan[] = [];
      for (const [code, price] of Object.entries(currentTier.value.prices)) {
        const priceMonthly = Number(price);
        const featureKeys = versionCatalog[code] ?? [];
        const isCurrent = !props.migrationMode && props.currentPlanCode === code;
        let relation: DisplayedPlan['relation'] = null;
        if (isCurrent) {
          relation = 'current';
        } else if (currentRank !== null) {
          const planRankValue = PLAN_RANK[code];
          if (planRankValue !== undefined) {
            relation = planRankValue > currentRank ? 'upgrade' : 'downgrade';
          }
        }
        plans.push({
          code,
          name: t(`billing.compare.planName.${code}`),
          tagline: t(`billing.compare.planTagline.${code}`),
          priceMonthly,
          features: featureKeys.map((key) => t(`billing.compare.feature.${version}.${key}`)),
          isCurrent,
          canResubscribe: false,
          isContactSales: false,
          relation,
        });
      }

      // V5 offer : always surface the "Pro" plan as "contact sales", even if
      // it is absent from the public pricing grid. Pro pricing is custom and
      // handled outside the standard subscription flow.
      const hasPro = plans.some((p) => p.code === 'pro');
      if (version === 'v5' && !hasPro) {
        plans.push({
          code: 'pro',
          name: t('billing.compare.planName.pro'),
          tagline: t('billing.compare.planTagline.pro'),
          priceMonthly: 0,
          features: (versionCatalog.pro ?? []).map(
            (key) => t(`billing.compare.feature.${version}.${key}`),
          ),
          isCurrent: !props.migrationMode && props.currentPlanCode === 'pro',
          canResubscribe: false,
          isContactSales: true,
          // Pro plan is contact-sales; relation isn't meaningful for the badge.
          relation: null,
        });
      }

      return plans;
    });

    // Recommended plan: Premium (best balance value/price). Highlighted
    // regardless of V4 vs V5 — only displayed if Premium exists in the grid.
    // Never recommend a contact-sales plan. Hidden once the shop already has
    // an active plan : the "current plan" ribbon carries the more relevant
    // context. Kept visible in migration mode (no « current plan » badge
    // there since the user lives on a different offer grid).
    const recommendedPlan = computed(() => {
      if (props.currentPlanCode && !props.migrationMode) return null;
      const codes = visiblePlans.value
        .filter((p) => !p.isContactSales)
        .map((p) => p.code);
      return codes.includes('premium') ? 'premium' : null;
    });

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
    const formatPrice = (n: number) => {
      if (n === 0) return t('billing.compare.free');
      const symbol = tiers.value?.currency === 'USD' ? '$' : '€';
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)} ${symbol}`;
    };

    /**
     * Whether a usable shop-level plan discount is active. Guards against
     * degenerate values (0% or 100% would either be a no-op or render a free
     * plan, neither of which is meaningful for the comparator).
     */
    const discountActive = computed<boolean>(() => {
      const pct = props.discountPercent;
      return pct != null && Number.isFinite(pct) && pct > 0 && pct < 100;
    });

    /**
     * Returns the post-discount amount for a given catalog price, or
     * `null` when no discount is active (so the template can fall back
     * to the plain catalog display).
     */
    const discountedPriceOf = (catalogPrice: number): number | null => {
      if (!discountActive.value) return null;
      const pct = props.discountPercent as number;
      return Math.round(catalogPrice * (1 - pct / 100) * 100) / 100;
    };

    /**
     * Yearly billing combined with an active plan discount is quote-only :
     * we can't surface a simple sticker price for that combination, so every
     * priced card switches to a "Nous contacter" display (like the Entreprise
     * card) and its CTA already routes to support in yearly mode.
     */
    const isYearlyDiscountContact = computed<boolean>(() => (
      localFrequency.value === 'yearly' && discountActive.value
    ));

    /**
     * Whether the auto-tier-upgrade block should be rendered right under
     * the tier dropdown. V5-only — hidden in migration mode (no auto-tier
     * UX yet) and in yearly (no self-served subscription).
     */
    const showAutoTierBlock = computed<boolean>(() => Boolean(
      tiers.value?.selectable
      && !props.migrationMode
      && localFrequency.value === 'monthly'
      && selectedTier.value,
    ));

    // Default plafond for auto-tier-upgrade = chosen tier + 3 paliers, or
    // the grid max if fewer than 3 tiers exist above. Returns 0 when the
    // shop is already at the top (no plafond to set). Centralised here so
    // the parent receives a correct value the moment the block becomes
    // visible — TierAutoUpgradeBlock is a pure presenter.
    const DEFAULT_PALIER_OFFSET = 3;
    const computeDefaultMaxTier = (tier: number): number => {
      if (!tiers.value) return 0;
      const sortedAbove = tiers.value.tiers
        .map((t) => t.threshold)
        .filter((th) => th > tier)
        .sort((a, b) => a - b);
      if (sortedAbove.length === 0) return 0;
      return sortedAbove[Math.min(DEFAULT_PALIER_OFFSET - 1, sortedAbove.length - 1)];
    };

    // Init / repair maxTier whenever the auto-tier block becomes visible or
    // the chosen tier changes. The parent owns the value (v-model); the
    // child block never self-initialises — this avoids the previous race
    // where the child emitted before the parent had wired the listener.
    watch(
      [showAutoTierBlock, selectedTier, () => tiers.value?.tiers.length ?? 0],
      ([visible, tier]) => {
        if (!visible || !tier) return;
        const valid = props.tierAutoUpgradeMax > tier
          && (tiers.value?.tiers.some((t) => t.threshold === props.tierAutoUpgradeMax) ?? false);
        if (valid) return;
        const def = computeDefaultMaxTier(tier);
        if (def !== props.tierAutoUpgradeMax) {
          emit('update:tierAutoUpgradeMax', def);
        }
      },
      { immediate: true },
    );

    /**
     * `true` when the user has selected a tier strictly greater than the
     * shop's natural tier (= more contacts than currently used). In V5, on
     * the *current* plan, this is the « modify tier only » path : same plan
     * but bigger capacity, charged at prorata. Cf. spec V5-i.
     */
    const isUpgradingTierOnly = computed<boolean>(() => {
      const min = minSelectableTier.value;
      if (min == null || selectedTier.value == null) return false;
      return selectedTier.value > min;
    });

    /**
     * The CTA label is contextual to the user's current situation:
     *   - On the same plan, tier inchangé → « Offre actuelle » (button disabled).
     *   - On the same plan, tier supérieur → « Modifier mon palier » (paid prorata).
     *   - Pro / contact-sales              → « Nous contacter ».
     *   - Upgrade vs current plan          → « Passer à {plan} » (immediate paid switch).
     *   - Downgrade vs current             → « Programmer {plan} » (effective next renewal).
     *   - No current plan at all           → « Souscrire à {plan} » (first subscription).
     */
    const ctaLabel = (plan: DisplayedPlan) => {
      if (plan.isContactSales) return t('billing.compare.cta.contact');
      // Yearly plans are quote-only — route every CTA to Gleap support.
      // No prorata / no self-served subscription on yearly from the front.
      if (localFrequency.value === 'yearly') return t('billing.compare.cta.contact');
      // Migration mode: every plan is a target migration choice — no current /
      // upgrade / downgrade distinction (the user's current offer lives on the
      // V4 grid, irrelevant for the V5 picker).
      if (props.migrationMode) {
        return t('billing.compare.cta.migrate', { plan: plan.name });
      }
      if (plan.isCurrent) {
        // Same plan, but the user picked a higher tier than their natural one
        // → propose to change the tier only (prorata charged like an upgrade).
        return isUpgradingTierOnly.value
          ? t('billing.compare.cta.modifyTier')
          : t('billing.compare.cta.current');
      }
      if (plan.relation === 'upgrade') {
        return t('billing.compare.cta.upgrade', { plan: plan.name });
      }
      if (plan.relation === 'downgrade') {
        return t('billing.compare.cta.downgrade', { plan: plan.name });
      }
      // No relation = user has no active plan; this is a first subscription.
      return t('billing.compare.cta.subscribe', { plan: plan.name });
    };

    /**
     * Whether the CTA button is interactive. The "current plan" CTA is normally
     * disabled, but becomes enabled when the user has bumped the tier above
     * the natural one (modify-tier-only path).
     */
    const ctaDisabled = (plan: DisplayedPlan): boolean => {
      if (plan.isContactSales) return false;
      if (plan.isCurrent && !plan.canResubscribe) {
        return !isUpgradingTierOnly.value;
      }
      return false;
    };

    /**
     * Opens the Gleap support widget. Used by "Pro" plan CTA — pricing is
     * custom and handled outside the standard flow.
     */
    const openContactSales = () => {
      try {
        Gleap.open();
      } catch {
        // Gleap not initialised — fallback to mailto on the support address.
        window.open('mailto:contact@shopimind.com?subject=Demande%20offre%20Pro', '_blank');
      }
    };

    const selectPlan = (code: string) => {
      emit('update:modelValue', code);
    };

    /**
     * Triggered by the "Choisir" CTA inside each plan card. Sets the model
     * AND immediately tells the parent to move to the next phase.
     */
    const confirmPlan = (code: string) => {
      emit('update:modelValue', code);
      emit('confirm', code);
    };

    watch(selectedTier, (v) => {
      emit('update:targetTier', v ?? undefined);
    });

    watch(() => props.frequency, (v) => {
      localFrequency.value = v;
    });

    onMounted(() => loadTiers());

    return {
      t,
      tiers,
      loading,
      localFrequency,
      selectedTier,
      tierOptions,
      visiblePlans,
      recommendedPlan,
      showAutoTierBlock,
      setFrequency,
      selectPlan,
      confirmPlan,
      openContactSales,
      formatNumber,
      formatPrice,
      discountedPriceOf,
      isYearlyDiscountContact,
      ctaLabel,
      ctaDisabled,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.plan-comparator {
  // Migration mode banner — shows the user's current offer above the V5 grid.
  &__current-banner {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    margin-bottom: 18px;
    background: linear-gradient(135deg, #f1f5f9 0%, #ffffff 100%);
    border: 1px solid #e2e8f0;
    border-radius: 12px;
  }

  &__current-banner-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }

  &__current-banner-body {
    flex: 1;
    min-width: 0;
  }

  &__current-banner-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #64748b;
  }

  &__current-banner-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    margin-top: 2px;
  }

  &__current-banner-price {
    font-weight: 500;
    color: #475569;
  }

  &__current-banner-subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin-top: 2px;
  }

  &__loading {
    text-align: center;
    padding: 60px 0;
  }

  &__empty {
    text-align: center;
    padding: 40px;
    color: #6b7280;
    background: #fbfaf7;
    border-radius: 12px;
  }

  // Frequency toggle
  &__frequency {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }

  &__pill-group {
    display: inline-flex;
    background: #f3f4f6;
    border-radius: 100px;
    padding: 4px;
    gap: 0;

    // Used when embedded inside the V5 tier-selector — give it a white
    // background so it stands out against the green tier panel.
    &--compact {
      background: #fff;
      border: 1px solid #ecf3e3;
    }
  }

  &__pill {
    border: none;
    background: transparent;
    padding: 8px 18px;
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    border-radius: 100px;
    transition: all 160ms ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover:not(.plan-comparator__pill--active) {
      color: #1f2937;
    }

    &--active {
      background: #fff;
      color: #1f2937;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
  }

  &__pill-badge {
    background: $brand-color-primary;
    color: #fff;
    font-size: 0.625rem;
    padding: 2px 6px;
    border-radius: 100px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  // Top info bar (V5) — 3-column control strip. The tier dropdown is the
  // primary V5 decision lever, so the whole bar is given a tinted background
  // and the dropdown column is made visually prominent (larger font, primary
  // border, focus ring) to draw the eye. The tier column may grow taller
  // than the others (it nests the auto-upgrade controls), so we centre the
  // side columns against the dropdown row at the top — handled with explicit
  // padding-top on those columns rather than flex align tricks.
  &__topbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    flex-wrap: wrap;
    padding: 18px 22px;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #f7fbf2 0%, #fbfdf6 100%);
    border: 1px solid #d9e9bf;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(98, 157, 3, 0.04);
  }

  &__topbar-info {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.875rem;
    color: #1f2937;
    flex-shrink: 0;
    // Aligns with the tier dropdown row (eyebrow label height + gap).
    padding-top: 22px;
  }

  &__topbar-icon {
    width: 36px;
    height: 36px;
    background: rgba(148, 200, 64, 0.2);
    color: #629d03;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9375rem;
  }

  &__topbar-text {
    font-weight: 600;
    color: #0f172a;
  }

  // The tier dropdown is the star of the bar — bigger, primary outline.
  &__topbar-tier {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 320px;
    min-width: 280px;
    max-width: 460px;
  }

  &__topbar-dropdown {
    width: 100%;

    :deep(.p-dropdown) {
      width: 100%;
      border: 2px solid $brand-color-primary;
      background: #fff;
      box-shadow: 0 1px 3px rgba(148, 200, 64, 0.18);
      transition: box-shadow 160ms ease;
    }

    :deep(.p-dropdown:hover),
    :deep(.p-dropdown:focus-within) {
      box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.22);
    }

    :deep(.p-dropdown-label) {
      padding: 12px 14px;
      font-size: 1rem;
      font-weight: 700;
      color: #0f172a;
      // PrimeVue centers the selected value by default in some skins.
      // Force left alignment to stay consistent with the rest of the
      // form fields in the topbar.
      text-align: left;
    }

    :deep(.p-dropdown-trigger) {
      color: $brand-color-primary;
    }
  }

  &__topbar-frequency {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;
  }

  &__topbar-eyebrow {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    color: #629d03;
    // Force left alignment — the topbar column may inherit a centered
    // text-align from a parent dialog style otherwise.
    text-align: left;
  }

  &__metric-readonly {
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
    margin-bottom: 20px;

    i {
      margin-right: 6px;
    }
  }

  // Plans grid
  &__plans {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
  }
}

// Plan card
.plan-card {
  position: relative;
  background: #fff;
  border: 1.5px solid #eaeaea;
  border-radius: 14px;
  padding: 18px 16px;
  cursor: pointer;
  transition: all 160ms ease;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: #cbd5e1;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.12);
  }

  &--selected {
    border-color: $brand-color-primary;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.16);
  }

  &--recommended {
    border-color: $brand-color-primary;
    background: linear-gradient(180deg, #ffffff 0%, #fafff5 100%);

    &:hover {
      box-shadow: 0 8px 24px -8px rgba(148, 200, 64, 0.32);
    }
  }

  // Contact-sales variant (e.g. "Pro" V5) — premium look, no price.
  &--contact {
    background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
    border-color: #1e293b;
    color: #fff;

    .plan-card__name,
    .plan-card__price-amount,
    .plan-card__feature {
      color: #fff;
    }

    .plan-card__tagline,
    .plan-card__price-period,
    .plan-card__feature-icon {
      color: rgba(255, 255, 255, 0.65);
    }

    .plan-card__price-amount--contact {
      color: #c1e07e;
    }

    &:hover {
      box-shadow: 0 12px 32px -12px rgba(15, 23, 42, 0.5);
    }
  }

  &__ribbon {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: $brand-color-primary;
    color: #fff;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 100px;
    white-space: nowrap;

    // Same chrome as the recommended ribbon, blue tint for the user's
    // currently active plan.
    &--current {
      background: #3b82f6;
    }
  }

  &__header {
    margin-bottom: 12px;
  }

  &__name {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
  }

  &__tagline {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 4px 0 0;
    line-height: 1.4;
  }

  &__price {
    margin: 8px 0 16px;
  }

  &__price-amount {
    font-size: 1.75rem;
    font-weight: 800;
    color: #1f2937;
    letter-spacing: -0.02em;
    line-height: 1.1;
    display: inline-flex;
    align-items: baseline;
    gap: 6px;

    &--contact {
      font-size: 1.375rem;
      color: $brand-color-primary;
    }
  }

  // Catalog price shown struck-through above the discounted amount when
  // a shop-level discount is active. Smaller + grey so the discounted
  // price stays the visual focus.
  &__price-strike {
    font-size: 0.95rem;
    color: #9ca3af;
    text-decoration: line-through;
    line-height: 1.1;
    margin-bottom: 2px;
  }

  // Small "-X%" pill rendered inline next to the discounted price.
  // Brand-green tint matches the other "discount" surfaces (hero card,
  // wallet panel) and keeps the comparator visually consistent.
  &__price-discount {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 100px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    background: rgba(148, 200, 64, 0.18);
    color: #5d8a24;
    line-height: 1.2;
  }

  &__price-period {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-top: 2px;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  &__feature {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 0.78125rem;
    color: #4b5563;
    line-height: 1.4;
  }

  &__feature-icon {
    color: $brand-color-primary;
    font-size: 0.875rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__cta {
    width: 100%;
    font-weight: 600 !important;
  }
}
</style>
